import {Link, RunInAction, RunInAction_Set, TextPlus} from "web-vcore";
import moment from "moment";
import {Button, CheckBox, Column, Div, Grid, Row, Select, Spinner, Text, TextInput} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {AddJournalEntry} from "../../Server/Commands/JournalEntryCommands.js";
import {GetJournalEntryAllText, GetWordsInDreamEntry_Map, GetWordsInDreamSegment_Map, JournalEntry, JournalSegment} from "../../Server/DB/journalEntries/@JournalEntry.js";
import {Me} from "../../Server/DB/users.js";
import {store} from "../../Store/index.js";
import {ES} from "web-vcore";
import {ShowSignInPopup} from "../@Shared/NavBar/UserPanel.js";
import {BaseComponent} from "react-vextensions";
import {JournalDisplayMode, JournalSort} from "../../Store/main/journal.js";
import {GetEntries} from "js-vextensions";
import React, {useState} from "react";
import {observer} from "mobx-react";
import {E, StartDownload} from "js-vextensions";
import {EntryListSettingsDropdown} from "./EntryList/EntryListSettingsDropdown.js";
import {DoesSegmentMatchFilter, JournalEntryPreview} from "./JournalEntryPreview.js";
import {EstimateDreamEndTime} from "../../Server/DB/journalEntries.js";
import {observer_mgl} from "mobx-graphlink";
import {GetWordsInText_Array_Deduped} from "../../Utils/Search/StringToWords.js";
import {MergeSets} from "../../Utils/General/General.js";
import {JournalDisplayModeSwitcher} from "./JournalUI.js";
import {ParseJournalFilterText, TimeRangeMatchesDateFilters} from "./JournalFilterText.js";
import {EstimateJournalEntryStartTime} from "../../Server/DB/@Shared/JournalTimeEstimates.js";

export const GetEntriesToShow_EntryList = (entries: JournalEntry[], sort: JournalSort, sort_desc: boolean)=>{
	const filter = store.main.journal.filter;
	const parsedFilterText = ParseJournalFilterText(filter.text_include);
	const filterWords = GetWordsInText_Array_Deduped(parsedFilterText.text_forWords, false);

	const entrySegmentsMatching = new Map<string, JournalSegment[]>();

	return entries
		.filter(entry=>{
			if (filter.showOnlyLucids && !entry.segments.Any(a=>a.lucid)) return false;
			if (parsedFilterText.dateRanges.length) {
				// match against the entry's full time-span (sleep -> wake), so an entry spanning midnight matches either date
				const entryStartTime = EstimateJournalEntryStartTime(entry);
				const entryEndTime = Math.max(entryStartTime, EstimateDreamEndTime(entry) ?? entryStartTime);
				if (!TimeRangeMatchesDateFilters(entryStartTime, entryEndTime, parsedFilterText.dateRanges)) return false;
			}
			if (filterWords.length) {
				const entryWords = GetWordsInDreamEntry_Map(entry, "both", true);
				if (!filterWords.every(word=>entryWords.has(word))) return false;
			}
			const segmentsMatchingFilter = entry.segments.filter(segment=>{
				// Why do we set applyGeneralTextFiltering=false here? Because if we're searching for two terms, but
				// ...the matches are split between two segments, we still want the journal-entry itself to match/show.
				return DoesSegmentMatchFilter(segment, filter, false);
			});
			entrySegmentsMatching.set(entry.id, segmentsMatchingFilter);
			if (segmentsMatchingFilter.length == 0) return false;
			return true;
		})[sort_desc ? "OrderByDescending" : "OrderBy"](entry=>{
			if (sort == JournalSort.date) return EstimateJournalEntryStartTime(entry);
			if (sort == JournalSort.longText_length) {
				if (filter.filterWithinJournalEntry) return [0, ...(entrySegmentsMatching.get(entry.id)?.map(a=>a.longText.length) ?? [])].Max();
				return [0, ...entry.segments.map(a=>a.longText.length)].Sum();
			}
		});
};

export const EntryList = observer_mgl((props: {entries: JournalEntry[]})=>{
	const {entries} = props;
	const uiState1 = store.main.journal;
	const uiState2 = store.main.engine.journey;

	const entriesToShow_noLimit = GetEntriesToShow_EntryList(entries, uiState1.sortBy, uiState1.sort_desc);
	const entriesToShow = entriesToShow_noLimit.slice(0, uiState1.limit);

	return (
		<Column style={ES({height: "100%", flex: 30})}>
			<Row className="clickThrough" style={{flexWrap: "wrap", gap: 5, padding: 10, background: "rgba(0,0,0,.7)", borderRadius: "10px 10px 0 0"}}>
				<JournalDisplayModeSwitcher/>
				<CheckBox text="Text wrap" value={uiState1.textPreviewMultiline} onChange={val=>RunInAction_Set(()=>uiState1.textPreviewMultiline = val)}/>
				<CheckBox text="Only lucids" value={uiState1.filter.showOnlyLucids} onChange={val=>RunInAction_Set(()=>uiState1.filter.showOnlyLucids = val)}/>
				<Row>
					<TextPlus info={`
						Searches at the word and word-shard level, case-insensitively. Examples...
						* Search for "authorize" matches "pre-authorize". ("pre-authorize" parsed into "pre-authorize", "pre", and "authorize")
						* Search for "author" does NOT match "pre-authorize".
						* Search for "tom" matches "Tom's hat". ("Tom's" parsed into "tom's", "tom", and "s")

						A "date:" token filters by entry date instead of by words. Examples...
						* "date:2026-05-01" matches entries on 2026-05-01.
						* "date:2026-05-01+-5" matches entries within 5 days of 2026-05-01. (either side)
						* "date:2026-05-01+5" matches entries from 2026-05-01 to 5 days after it.
						* "date:2026-05-01-5" matches entries from 5 days before 2026-05-01, up to that date.
						(these can be combined with words, eg. "flying date:2026-05-01+-5")
					`.AsMultiline(0)}>Filter:</TextPlus>
					<TextInput ml={5} style={{width: 260}} value={uiState1.filter.text_include} onChange={val=>RunInAction_Set(()=>uiState1.filter.text_include = val)}/>
				</Row>
				<Row>
					<Text>Sort:</Text>
					<Select ml={5} options={GetEntries(JournalSort, "ui")} value={uiState1.sortBy} onChange={val=>RunInAction_Set(()=>uiState1.sortBy = val)}/>
					<Button ml={5} mdIcon={uiState1.sort_desc ? "arrow-down" : "arrow-up"} onClick={()=>{
						RunInAction_Set(()=>uiState1.sort_desc = !uiState1.sort_desc);
					}}/>
				</Row>
				<Row>
					<Text>Limit:</Text>
					<Spinner ml={5} value={uiState1.limit} onChange={val=>RunInAction_Set(()=>uiState1.limit = val)}/>
					<Text ml={5}>Count: {entriesToShow_noLimit.length}</Text>
				</Row>

				<Row ml="auto">
					<Button size={32} mdIcon="plus" style={{justifySelf: "right"}} onClick={async()=>{
						if (Me() == null) return ShowSignInPopup();
						const entry = new JournalEntry({
							title: "New entry",
							wakeTime: Date.now(),
							segments: [
								new JournalSegment({
									wakeTime: Date.now(),
								}),
							],
						});
						const {id: entryID} = await new AddJournalEntry({entry}).RunOnServer();
						RunInAction("JournalEntryList.addEntry.onClick", ()=>store.main.journal.selectedJournalEntry = entryID);
					}}/>
					<Button ml={3} size={32} mdIcon={uiState2.showAnchorEntities ? "image" : "image-frame"} onClick={()=>{
						RunInAction_Set(()=>uiState2.showAnchorEntities = !uiState2.showAnchorEntities);
					}}/>
					<EntryListSettingsDropdown entries={entries}/>
				</Row>
			</Row>
			<ScrollView style={{flex: 1}} contentStyle={E(
				{backgroundColor: "rgba(30,30,30,.7)", borderRadius: "0 0 10px 10px"},
				uiState1.textPreviewMultiline && {minWidth: null},
			)} onClick={e=>{
				if (e.target != e.currentTarget) return;
				RunInAction("JournalEntryList.background.onClick", ()=>store.main.journal.selectedJournalEntry = null);
			}}>
				{entriesToShow.length == 0 && <div style={{textAlign: "center", fontSize: 18}}>No matching journal-entries.</div>}
				{entriesToShow.map((entry, index)=>{
					return <JournalEntryPreview key={entry.id} entry={entry} journalPage={true}/>;
				})}
			</ScrollView>
		</Column>
	);
});